import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../lib/cart";
import { formatRand, resolveImageUrl } from "../lib/api";

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 border-l hairline"
        style={{ background: "var(--bg)" }}
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b hairline flex-row items-center justify-between space-y-0">
          <SheetTitle className="font-display text-lg uppercase tracking-tight">
            Cart ({items.length})
          </SheetTitle>
          <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
          <button onClick={() => setOpen(false)} className="hover:opacity-60" data-testid="cart-close-btn">
            <X size={20} strokeWidth={1.5} />
          </button>
        </SheetHeader>
        <div className="flex flex-col h-[calc(100vh-160px)]">
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-6 text-center">
                <p className="text-sm text-black/55">Empty.</p>
              </div>
            ) : (
              <ul>
                {items.map((it, idx) => (
                  <li
                    key={`${it.product_id}-${it.size}-${it.color}-${idx}`}
                    className="flex gap-4 p-6 border-b hairline"
                    data-testid={`cart-item-${idx}`}
                  >
                    <div className="w-20 h-24 flex-shrink-0" style={{ background: "#fff" }}>
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="font-display uppercase text-sm tracking-tight truncate">{it.name}</div>
                      <div className="text-xs text-black/55 mt-1 truncate">{[it.color, it.size].filter(Boolean).join(" · ")}</div>
                      <div className="text-sm mt-1">{formatRand(it.price)}</div>
                      <div className="mt-auto flex items-center justify-between pt-3">
                        <div className="inline-flex items-center border hairline">
                          <button onClick={() => updateQty(idx, it.quantity - 1)} className="p-2 hover:bg-black/5" data-testid={`cart-qty-minus-${idx}`}>
                            <Minus size={14} />
                          </button>
                          <span className="px-3 text-sm font-display">{it.quantity}</span>
                          <button onClick={() => updateQty(idx, it.quantity + 1)} className="p-2 hover:bg-black/5" data-testid={`cart-qty-plus-${idx}`}>
                            <Plus size={14} />
                          </button>
                        </div>
                        <button onClick={() => removeItem(idx)} className="text-xs flex items-center gap-1 opacity-60 hover:opacity-100" data-testid={`cart-remove-${idx}`}>
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t hairline p-6 space-y-4" style={{ background: "var(--bg-alt)" }}>
            <div className="flex items-center justify-between">
              <span className="text-sm">Subtotal</span>
              <span className="font-display text-xl" data-testid="cart-subtotal">{formatRand(subtotal)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              className={`btn-primary w-full ${items.length === 0 ? "pointer-events-none opacity-40" : ""}`}
              data-testid="cart-checkout-btn"
            >
              Checkout
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
